import { observer } from 'mobx-react-lite'
import { EditPencil, Plus as PlusIcon, Trash } from 'iconoir-react'
import { compact } from 'lodash'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { PageScroll } from 'app/components/layout/PageScroll'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import SimpleTable from 'app/components/table/SimpleTable'
import { UtmAlias } from 'app/types/entities'

export type MappingMediaShadcnComponentProps = {
  media: UtmAlias[]
  loading: boolean
  canEditMapping: boolean
  onAdd: () => void
  onEdit: (utmAlias: UtmAlias) => void
  onDelete: (id: string, workspaceId: string) => Promise<void>
}

export function getMappingMediaShadcnColumns(ctx: {
  canEditMapping: boolean
  onEdit: (utmAlias: UtmAlias) => void
  onDelete: (id: string, workspaceId: string) => Promise<void>
}): ColumnDef<UtmAlias, any>[] {
  return compact([
    {
      id: `original`,
      header: `Medium`,
      accessorKey: `original`,
      enableHiding: false,
      enableResizing: false,
      meta: { defaultVisible: true, sortKey: `original.keyword` },
    },
    {
      id: `alias`,
      header: `Alias`,
      accessorKey: `alias`,
      enableHiding: false,
      enableResizing: false,
      meta: { defaultVisible: true, sortKey: `alias.keyword` },
    },
    ctx.canEditMapping && {
      id: `Actions`,
      header: `Actions`,
      enableSorting: false,
      enableHiding: false,
      enableResizing: false,
      size: 100,
      minSize: 100,
      cell: (props) => (
        <div className="flex justify-end gap-4">
          <TooltipWrapper message="Edit alias">
            <EditPencil
              className="h-5 w-5 cursor-pointer text-default hover:text-strong"
              onClick={() => ctx.onEdit(props.row.original)}
            />
          </TooltipWrapper>
          <TooltipWrapper message="Delete alias">
            <Trash
              className="h-5 w-5 cursor-pointer text-default hover:text-red-600"
              onClick={() => ctx.onDelete(props.row.original.id, props.row.original.workspaceId)}
            />
          </TooltipWrapper>
        </div>
      ),
      meta: { defaultVisible: true },
    },
  ])
}

export const MappingMediaShadcnComponent = observer(function MappingMediaShadcnComponent({
  media,
  loading,
  canEditMapping,
  onAdd,
  onEdit,
  onDelete,
}: MappingMediaShadcnComponentProps) {
  const columns = useMemo(
    () => getMappingMediaShadcnColumns({ canEditMapping, onEdit, onDelete }),
    [canEditMapping, onEdit, onDelete],
  )

  const [searchValue, setSearchValue] = useState(``)
  const filteredMedia = useMemo(() => {
    const needle = searchValue.trim().toLowerCase()
    if (!needle) return media
    return media.filter(
      (utmAlias) => utmAlias.original.toLowerCase().includes(needle) || utmAlias.alias.toLowerCase().includes(needle),
    )
  }, [media, searchValue])

  return (
    <PageScroll variant="table" contentClassName="pt-6">
      <SimpleTable
        columns={columns}
        data={loading ? undefined : filteredMedia}
        hideFooter
        emptyState={
          <TableEmptyState
            title="No medium aliases"
            description="Aliases map raw UTM medium values to a clean, consistent name in your reporting."
            action={
              canEditMapping ? (
                <Button variant="primary" size="sm" onClick={onAdd}>
                  <PlusIcon className="h-4 w-4" />
                  <span>New Alias</span>
                </Button>
              ) : undefined
            }
          />
        }
        onSearchValueChange={setSearchValue}
        edgePadded
        extraSlot={
          canEditMapping ? (
            <Button variant="primary" size="sm" onClick={onAdd}>
              <PlusIcon className="h-4 w-4" />
              <span>New Alias</span>
            </Button>
          ) : undefined
        }
      />
    </PageScroll>
  )
})
